/**
 * Type alias for values that can be used as Map keys or object property names.
 * Represents primitive types that can be used for indexing and key operations.
 */
export type KeyValue = string | number | symbol

export { partitionBy } from './reducer'
export { combineComparators } from './sort'

/**
 * Creates a new sorted array using the provided comparison function.
 * Does not modify the original array.
 *
 * @description This function creates a shallow copy of the input array before sorting,
 * ensuring the original array remains unchanged. It's useful for functional programming
 * patterns where immutability is important.
 *
 * @example
 * ```typescript
 * const numbers = [3, 1, 4, 1, 5]
 * const sorted = sortBy(numbers, (a, b) => a - b)
 * // Result: [1, 1, 3, 4, 5]
 * // Original unchanged: [3, 1, 4, 1, 5]
 *
 * const users = [
 *   { name: 'John', age: 30 },
 *   { name: 'Jane', age: 25 },
 *   { name: 'Bob', age: 35 }
 * ]
 * const sortedByAge = sortBy(users, (a, b) => a.age - b.age)
 * // [{ name: 'Jane', age: 25 }, { name: 'John', age: 30 }, { name: 'Bob', age: 35 }]
 * ```
 *
 * @param list - The array to sort
 * @param by - Comparison function that returns negative/zero/positive numbers
 * @returns A new sorted array
 * @template T - The type of array elements
 */
export function sortBy<T>(list: Array<T>, by: (a: T, b: T) => number): Array<T> {
  return Array.from(list).sort(by)
}

/**
 * Returns the first element of an array. Throws an error if the array is empty.
 * Designed for readability and composition as an alternative to `array[0]`.
 *
 * @description This function provides a readable, composable way to access the first
 * element of an array. Unlike bracket notation, it throws a clear error for empty arrays
 * and has a clean return type without undefined for better functional composition.
 *
 * @example
 * ```typescript
 * const numbers = [1, 2, 3, 4]
 * const firstNumber = first(numbers); // 1
 *
 * const users = [{ id: 1, name: 'John' }, { id: 2, name: 'Jane' }]
 * const firstUser = first(users); // { id: 1, name: 'John' }
 *
 * // Throws error for empty arrays
 * first([]); // Error: 'Array is empty'
 *
 * // Good for functional composition
 * const result = [1, 2, 3]
 *   .map(n => n * 2)
 *   .filter(n => n > 2)
 *   .pipe(first); // Clean, readable
 * ```
 *
 * @param array - The array to get the first element from
 * @returns The first element of the array
 * @throws Error when the array is empty
 * @template T - The type of array elements
 */
export function first<T>(array: Array<T>): T {
  if (array.length === 0) throw new Error('Array is empty')

  return array[0]
}

/**
 * Returns the last element of an array. Throws an error if the array is empty.
 * Designed for readability and composition as an alternative to `array[array.length - 1]`.
 *
 * @description This function provides a readable, composable way to access the last
 * element of an array. It throws a clear error for empty arrays and has a clean
 * return type without undefined for better functional composition.
 *
 * @example
 * ```typescript
 * const numbers = [1, 2, 3, 4]
 * const lastNumber = last(numbers); // 4
 *
 * const users = [{ id: 1, name: 'John' }, { id: 2, name: 'Jane' }]
 * const lastUser = last(users); // { id: 2, name: 'Jane' }
 *
 * // Throws error for empty arrays
 * last([]); // Error: 'Array is empty'
 *
 * // Useful for getting the most recent item
 * const recentTransactions = getTransactions().sort(byDate)
 * const mostRecent = last(recentTransactions)
 * ```
 *
 * @param array - The array to get the last element from
 * @returns The last element of the array
 * @throws Error when the array is empty
 * @template T - The type of array elements
 */
export function last<T>(array: Array<T>): T {
  if (array.length === 0) throw new Error('Array is empty')

  return array[array.length - 1]
}

/**
 * Creates an array with `size` elements containing values from `0` to `size - 1`.
 * Useful for creating numeric sequences and iterations.
 *
 * @description This function generates a sequence of consecutive integers starting from 0.
 * It's commonly used for creating indices, iteration counts, or test data.
 *
 * @example
 * ```typescript
 * range(0); // []
 * range(3); // [0, 1, 2]
 * range(5); // [0, 1, 2, 3, 4]
 *
 * // Common use cases
 * range(10).map(i => `Item ${i}`); // ['Item 0', 'Item 1', ..., 'Item 9']
 *
 * // Creating test data
 * const testUsers = range(5).map(i => ({ id: i, name: `User ${i}` }))
 *
 * // Pagination
 * const pageNumbers = range(totalPages).map(i => i + 1)
 * ```
 *
 * @param size - The number of elements to generate
 * @returns An array of numbers from 0 to size-1
 */
export function range(size: number): Array<number> {
  return Array.from(Array(size).keys())
}

/**
 * Returns a new array with only unique values, preserving the first occurrence of each value.
 *
 * @description This function eliminates duplicate values from an array using Set semantics.
 * The order of first occurrences is preserved. Works with any type that can be stored in a Set.
 *
 * @example
 * ```typescript
 * const numbers = [1, 2, 2, 3, 3, 3, 4]
 * const uniqueNumbers = unique(numbers); // [1, 2, 3, 4]
 *
 * const strings = ['apple', 'banana', 'apple', 'orange', 'banana']
 * const uniqueStrings = unique(strings); // ['apple', 'banana', 'orange']
 *
 * // Works with any type that can be stored in a Set
 * const booleans = [true, false, true, false]
 * const uniqueBooleans = unique(booleans); // [true, false]
 *
 * // Useful for cleaning up data
 * const userIds = transactions.map(t => t.userId)
 * const uniqueUserIds = unique(userIds)
 * ```
 *
 * @param list - The array to remove duplicates from
 * @returns A new array with unique values only
 * @template T - The type of array elements
 */
export function unique<T>(list: Array<T>): Array<T> {
  return [...new Set(list)]
}

/**
 * Returns unique values using a key extraction function.
 * If multiple items have the same key, only the last one is kept.
 *
 * @description This function removes duplicates based on a computed key rather than
 * the items themselves. It's useful when you want to deduplicate objects based on
 * a specific property or computed value.
 *
 * @example
 * ```typescript
 * const users = [
 *   { id: 1, name: 'John', role: 'admin' },
 *   { id: 2, name: 'Jane', role: 'user' },
 *   { id: 3, name: 'Bob', role: 'admin' },
 *   { id: 4, name: 'Alice', role: 'user' }
 * ]
 *
 * const uniqueByRole = uniqueBy(users, user => user.role)
 * // [{ id: 3, name: 'Bob', role: 'admin' }, { id: 4, name: 'Alice', role: 'user' }]
 * // Note: Keeps the last occurrence of each role
 *
 * const products = [
 *   { name: 'iPhone', category: 'phone', price: 999 },
 *   { name: 'Samsung', category: 'phone', price: 899 },
 *   { name: 'MacBook', category: 'laptop', price: 1299 }
 * ]
 *
 * const uniqueByCategory = uniqueBy(products, p => p.category)
 * // Keeps one product per category (the last one encountered)
 * ```
 *
 * @param list - The array to process
 * @param by - Function to extract the key for comparison
 * @returns Array with unique items based on the key function
 * @template K - The type of the key
 * @template T - The type of array elements
 */
export function uniqueBy<K, T>(list: Array<T>, by: (item: T) => K): Array<T> {
  const map: Map<K, T> = new Map()
  for (const item of list) {
    const key = by(item)
    map.set(key, item)
  }
  return [...map.values()]
}

/**
 * Groups array elements by a key function into a Map where values are arrays of grouped items.
 *
 * @description This function creates a Map where keys are computed by the provided function
 * and values are arrays containing all items that produced the same key. It's useful for
 * categorizing, organizing, and analyzing data by common properties.
 *
 * @example
 * ```typescript
 * const transactions = [
 *   { id: 1, category: 'food', amount: 25 },
 *   { id: 2, category: 'transport', amount: 15 },
 *   { id: 3, category: 'food', amount: 40 },
 *   { id: 4, category: 'entertainment', amount: 60 }
 * ]
 *
 * const byCategory = groupBy(transactions, t => t.category)
 * // Map {
 * //   'food' => [{ id: 1, category: 'food', amount: 25 }, { id: 3, category: 'food', amount: 40 }],
 * //   'transport' => [{ id: 2, category: 'transport', amount: 15 }],
 * //   'entertainment' => [{ id: 4, category: 'entertainment', amount: 60 }]
 * // }
 *
 * // Calculate totals per category
 * for (const [category, items] of byCategory) {
 *   const total = sum(items.map(item => item.amount))
 *   console.log(`${category}: $${total}`)
 * }
 *
 * // Group by computed values
 * const numbers = [1, 2, 3, 4, 5, 6]
 * const evenOdd = groupBy(numbers, n => n % 2 === 0 ? 'even' : 'odd')
 * // Map { 'odd' => [1, 3, 5], 'even' => [2, 4, 6] }
 * ```
 *
 * @param list - The array to group
 * @param by - Function to compute the grouping key
 * @returns A Map with keys and arrays of grouped items
 * @template K - The type of the grouping key (must be a valid Map key)
 * @template T - The type of array elements
 */
export function groupBy<K, T>(list: Array<T>, by: (item: T) => K): Map<K, Array<T>> {
  const groups = new Map<K, Array<T>>()

  for (const item of list) {
    const key = by(item)
    const group = groups.get(key)
    if (group) {
      group.push(item)
    } else {
      groups.set(key, [item])
    }
  }

  return groups
}

/**
 * Flattens arrays stored as Map values and returns unique values.
 * Useful for collecting distinct items from grouped data.
 *
 * @description This function takes a Map where values are arrays, flattens all those arrays
 * into a single array, and then removes duplicates. It's commonly used after grouping
 * operations when you want to collect all unique items across all groups.
 *
 * @example
 * ```typescript
 * const teamMembers = new Map([
 *   ['frontend', ['alice', 'bob', 'charlie']],
 *   ['backend', ['david', 'alice', 'eve']],
 *   ['mobile', ['bob', 'frank']]
 * ])
 *
 * const allMembers = flattenUniqueMapValues(teamMembers)
 * // ['alice', 'bob', 'charlie', 'david', 'eve', 'frank']
 *
 * // Common pattern with groupBy
 * const users = [
 *   { name: 'John', skills: ['JS', 'React'] },
 *   { name: 'Jane', skills: ['Python', 'JS'] },
 *   { name: 'Bob', skills: ['Java', 'React'] }
 * ]
 * const skillGroups = groupBy(users, user => user.name)
 * // Then collect all unique skills across all users
 * ```
 *
 * @param map - Map with array values to flatten and deduplicate
 * @returns Array of unique values from all arrays
 * @template K - The type of Map keys
 * @template V - The type of array elements (must be usable as Set values)
 */
export function flattenUniqueMapValues<K, V extends KeyValue>(map: Map<K, Array<V>>): Array<V> {
  return unique([...map.values()].flat())
}

/**
 * Filters out all falsy values (null, undefined, false, 0, '', NaN) and returns
 * an array with proper typing.
 *
 * @description This function removes all falsy values from an array and updates the
 * TypeScript type to reflect that falsy values have been removed. Falsy values include
 * null, undefined, false, 0, empty string, and NaN.
 *
 * @example
 * ```typescript
 * const mixed = [1, null, 'hello', undefined, 0, 'world', false, '', NaN, 42]
 * const truthy = filterTruthy(mixed)
 * // [1, 'hello', 'world', 42]
 *
 * // Common use case: filtering API responses
 * const apiResults = [
 *   { id: 1, name: 'Valid' },
 *   null,
 *   { id: 2, name: 'Also Valid' },
 *   undefined
 * ]
 * const validResults = filterTruthy(apiResults)
 * // [{ id: 1, name: 'Valid' }, { id: 2, name: 'Also Valid' }]
 *
 * // Note: This removes ALL falsy values, including 0, false, and ''
 * const values = [1, 0, true, false, 'hello', '', null]
 * const truthyValues = filterTruthy(values); // [1, true, 'hello']
 * ```
 *
 * @param list - The array to filter
 * @returns Array with all falsy values removed
 * @template T - The type of array elements (excluding null/undefined)
 */
export function filterTruthy<T>(list: Array<T | null | undefined>): Array<T> {
  return list.filter(Boolean) as Array<T>
}

/**
 * Keeps the items whose text contains the search term, ignoring case. Made for search fields.
 *
 * @description The term is trimmed and compared case-insensitively (locale-aware) against the text
 * returned by `by`, matching anywhere in it. An empty or whitespace-only term keeps every item and
 * returns the original list, so an untouched search field shows everything.
 *
 * @example
 * ```typescript
 * const teachers = [
 *   { id: '1', nickname: 'Ann Kenter' },
 *   { id: '2', nickname: 'Leo Mango' },
 * ]
 *
 * filterBySearch(teachers, 'mango', (teacher) => teacher.nickname)
 * // [{ id: '2', nickname: 'Leo Mango' }]
 *
 * filterBySearch(teachers, '  ANN ', (teacher) => teacher.nickname)
 * // [{ id: '1', nickname: 'Ann Kenter' }]
 *
 * filterBySearch(teachers, '   ', (teacher) => teacher.nickname)
 * // both teachers (blank term)
 * ```
 *
 * @param list - The array to filter
 * @param searchTerm - What the user typed
 * @param by - Function returning the text to search in for each item
 * @returns The matching items, in their original order
 * @template T - The type of array elements
 */
export function filterBySearch<T>(list: Array<T>, searchTerm: string, by: (item: T) => string): Array<T> {
  const normalizedTerm = searchTerm.trim().toLocaleLowerCase()

  if (!normalizedTerm) {
    return list
  }

  return list.filter((item) => by(item).toLocaleLowerCase().includes(normalizedTerm))
}

/**
 * Extracts the value of a specific property from an object. Type-safe property access.
 *
 * @description This function provides a simple, type-safe way to extract a property value
 * from an object. While it might seem unnecessary for simple cases, it's useful for
 * functional programming patterns and when you need a function reference.
 *
 * @example
 * ```typescript
 * const user = { id: 1, name: 'John', email: 'john@example.com' }
 * const name = pick(user, 'name'); // 'John'
 * const id = pick(user, 'id'); // 1
 *
 * // TypeScript ensures property exists
 * // pick(user, 'nonexistent'); // TypeScript error
 *
 * // Useful in functional programming
 * const users = [{ name: 'John' }, { name: 'Jane' }]
 * const names = users.map(user => pick(user, 'name'))
 * // or more elegantly with pickFromList
 * ```
 *
 * @param item - The object to extract the property from
 * @param property - The property key to extract
 * @returns The value of the specified property
 * @template T - The type of the input object
 * @template U - The type of the property key (constrained to keys of T)
 */
export function pick<T, U extends keyof T>(item: T, property: U): T[U] {
  return item[property]
}

/**
 * Extracts a specific property value from each object in an array.
 * Similar to `Array.map()` but specifically for property extraction.
 *
 * @description This function maps over an array and extracts the same property from each
 * object. If some objects don't have the property or it's undefined, those values will
 * be included as undefined in the result array.
 *
 * @example
 * ```typescript
 * const users = [
 *   { id: 1, name: 'Alice', age: 25 },
 *   { id: 2, name: 'Bob', age: 30 },
 *   { id: 3, name: 'Charlie', age: 35 }
 * ]
 *
 * const names = pickFromList(users, 'name'); // ['Alice', 'Bob', 'Charlie']
 * const ids = pickFromList(users, 'id'); // [1, 2, 3]
 * const ages = pickFromList(users, 'age'); // [25, 30, 35]
 *
 * // Handles objects with missing properties
 * const mixed = [{ name: 'John' }, { id: 2 }, { name: 'Jane', id: 3 }]
 * const names = pickFromList(mixed, 'name'); // ['John', undefined, 'Jane']
 *
 * // Useful for data transformation
 * const transactions = getTransactions()
 * const amounts = pickFromList(transactions, 'amount')
 * const total = sum(amounts)
 * ```
 *
 * @param list - The array of objects to extract properties from
 * @param property - The property key to extract from each object
 * @returns Array of property values (may include undefined)
 * @template T - The type of objects in the array
 * @template U - The type of the property key (constrained to keys of T)
 */
export function pickFromList<T, U extends keyof T>(list: ReadonlyArray<T>, property: U): Array<T[U]> {
  return list.map((item) => pick(item, property))
}

/**
 * Extracts unique property values from an array of objects.
 * Combines `pickFromList` and `unique` for convenient property extraction.
 *
 * @description This function extracts a specific property from each object in an array
 * and returns only the unique values. It's useful for getting distinct values of a
 * property across a collection of objects.
 *
 * @example
 * ```typescript
 * const orders = [
 *   { id: 1, customerId: 'cust1', status: 'pending' },
 *   { id: 2, customerId: 'cust2', status: 'completed' },
 *   { id: 3, customerId: 'cust1', status: 'pending' },
 *   { id: 4, customerId: 'cust3', status: 'completed' }
 * ]
 *
 * const uniqueCustomerIds = pickUnique(orders, 'customerId')
 * // ['cust1', 'cust2', 'cust3']
 *
 * const uniqueStatuses = pickUnique(orders, 'status')
 * // ['pending', 'completed']
 *
 * // Useful for analytics and filtering
 * const products = getProducts()
 * const availableCategories = pickUnique(products, 'category')
 * const availableBrands = pickUnique(products, 'brand')
 * ```
 *
 * @param list - The array of objects to extract properties from
 * @param property - The property key to extract and deduplicate
 * @returns Array of unique property values
 * @template T - The type of objects in the array
 * @template U - The type of the property key (constrained to keys of T)
 */
export function pickUnique<T, U extends keyof T>(list: Array<T>, property: U): Array<T[U]> {
  return unique(pickFromList(list, property))
}

/**
 * Creates a Map from an array using a key function.
 * Keys are calculated by the provided function, values are the original items.
 *
 * @description This function converts an array into a Map for efficient key-based lookups.
 * If multiple items generate the same key, later items will overwrite earlier ones.
 * This is useful for creating lookup tables and enabling O(1) access to items by key.
 *
 * @example
 * ```typescript
 * const users = [
 *   { id: 'u1', name: 'Alice', role: 'admin' },
 *   { id: 'u2', name: 'Bob', role: 'user' },
 *   { id: 'u3', name: 'Charlie', role: 'moderator' }
 * ]
 *
 * const userMap = toMapWithKey(users, user => user.id)
 * // Map {
 * //   'u1' => { id: 'u1', name: 'Alice', role: 'admin' },
 * //   'u2' => { id: 'u2', name: 'Bob', role: 'user' },
 * //   'u3' => { id: 'u3', name: 'Charlie', role: 'moderator' }
 * // }
 *
 * // Fast lookups - O(1) access
 * const alice = userMap.get('u1'); // { id: 'u1', name: 'Alice', role: 'admin' }
 *
 * // Example with computed keys
 * const numbers = [10, 20, 30]
 * const doubledKeys = toMapWithKey(numbers, n => n * 2)
 * // Map { 20 => 10, 40 => 20, 60 => 30 }
 * ```
 *
 * @param list - The array to convert to a Map
 * @param by - Function to compute the key for each item
 * @returns A Map with computed keys and original items as values
 * @template K - The type of the key (must be a valid Map key)
 * @template T - The type of array elements
 */
export function toMapWithKey<K extends KeyValue, T>(list: Array<T>, by: (item: T) => K): Map<K, T> {
  const map: Map<K, T> = new Map()
  for (const item of list) {
    const key = by(item)
    map.set(key, item)
  }
  return map
}

/**
 * Converts a Map's values back to an array, discarding the keys.
 *
 * @description This function extracts all values from a Map and returns them as an array.
 * The keys are discarded, and only the values are preserved. The order follows Map
 * insertion order.
 *
 * @example
 * ```typescript
 * const userMap = new Map([
 *   ['u1', { id: 'u1', name: 'Alice' }],
 *   ['u2', { id: 'u2', name: 'Bob' }],
 *   ['u3', { id: 'u3', name: 'Charlie' }]
 * ])
 *
 * const users = mapToList(userMap)
 * // [{ id: 'u1', name: 'Alice' }, { id: 'u2', name: 'Bob' }, { id: 'u3', name: 'Charlie' }]
 *
 * const stringMap = new Map([
 *   ['a', 'alpha'],
 *   ['b', 'beta'],
 *   ['c', 'gamma']
 * ])
 *
 * const values = mapToList(stringMap); // ['alpha', 'beta', 'gamma']
 *
 * // Common pattern: Map -> process -> back to array
 * const processed = someArray
 *   .pipe(arr => toMapWithKey(arr, item => item.id))
 *   .pipe(processMap)
 *   .pipe(mapToList)
 * ```
 *
 * @param map - The Map to extract values from
 * @returns Array containing all Map values
 * @template K - The type of Map keys
 * @template T - The type of Map values
 */
export function mapToList<K extends KeyValue, T>(map: Map<K, T>): Array<T> {
  return Array.from(map.values())
}

/**
 * Splits an array into chunks of the specified size. Returns smaller final chunk
 * if array length is not divisible by size.
 *
 * @description This function divides a large array into smaller, manageable pieces of
 * equal size (except possibly the last chunk). It's commonly used for batch processing,
 * pagination, or when you need to process large datasets in smaller groups.
 *
 * @example
 * ```typescript
 * const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9]
 * chunk(numbers, 3); // [[1, 2, 3], [4, 5, 6], [7, 8, 9]]
 * chunk(numbers, 4); // [[1, 2, 3, 4], [5, 6, 7, 8], [9]]
 *
 * // Default chunk size is 10
 * const longArray = range(25)
 * chunk(longArray); // Three chunks: [0-9], [10-19], [20-24]
 *
 * // Edge cases
 * chunk([], 5); // []
 * chunk([1, 2, 3], 0); // [[1, 2, 3]] (returns whole array)
 * chunk([1, 2, 3], NaN); // [[1, 2, 3]] (returns whole array)
 *
 * // Batch processing example
 * const users = getAllUsers()
 * const batches = chunk(users, 100)
 * for (const batch of batches) {
 *   await processBatch(batch); // Process 100 users at a time
 * }
 * ```
 *
 * @param list - The array to split into chunks
 * @param size - The size of each chunk (default: 10)
 * @returns Array of arrays, each containing up to `size` elements
 * @template T - The type of array elements
 */
export function chunk<T>(list: Array<T>, size = 10): Array<Array<T>> {
  if (size === 0 || isNaN(size)) {
    return [list]
  }

  return list.reduce((chunks: Array<Array<T>>, item: T, index: number): Array<Array<T>> => {
    const current = Math.floor(index / size)

    if (!chunks[current]) {
      chunks[current] = [item]
    } else {
      chunks[current].push(item)
    }

    return chunks
  }, [])
}

/**
 * Removes only `null` and `undefined` values, preserving other falsy values
 * like `0`, `false`, and empty strings.
 *
 * @description This function is more specific than `filterTruthy` as it only removes
 * nullish values (null and undefined) while keeping other falsy values that might
 * be meaningful (like 0 for counts, false for boolean flags, or empty strings).
 *
 * @example
 * ```typescript
 * const mixed = [0, false, undefined, '', null, 'pizza', NaN]
 * const withoutNullish = removeNullish(mixed)
 * // [0, false, '', 'pizza', NaN]
 *
 * // More specific than filterTruthy
 * const scores = [0, 85, null, 92, undefined, 0, 78]
 * const validScores = removeNullish(scores); // [0, 85, 92, 0, 78]
 *
 * // Useful for optional object properties
 * const users = [
 *   { name: 'John', email: 'john@example.com' },
 *   { name: 'Jane', email: null },
 *   { name: 'Bob', email: undefined },
 *   { name: 'Alice', email: '' }
 * ]
 * const emails = removeNullish(users.map(u => u.email))
 * // ['john@example.com', ''] - keeps empty string but removes null/undefined
 * ```
 *
 * @param array - The array to filter
 * @returns Array with null and undefined values removed
 * @template T - The type of array elements (excluding null/undefined)
 */
export function removeNullish<T>(array: Array<T | undefined | null>): Array<T> {
  return array.filter((value): value is T => value !== undefined && value !== null)
}

export function getRandomValue<T>(array: Array<T>): T {
  return array[Math.floor(Math.random() * array.length)]
}
/**
 * Returns elements that exist in both arrays based on a key function.
 * Returns elements from `list2` that have matching keys in `list1`.
 *
 * @description This function performs a set intersection operation using a key function
 * to determine equality. It's useful for finding common elements between two arrays
 * when the elements themselves aren't directly comparable but have comparable properties.
 *
 * @example
 * ```typescript
 * const currentUsers = [
 *   { id: 1, name: 'Alice' },
 *   { id: 2, name: 'Bob' },
 *   { id: 3, name: 'Charlie' }
 * ]
 *
 * const activeUsers = [
 *   { id: 2, lastActive: '2024-01-15' },
 *   { id: 3, lastActive: '2024-01-14' },
 *   { id: 4, lastActive: '2024-01-13' }
 * ]
 *
 * const currentActiveUsers = intersectBy(currentUsers, activeUsers, user => user.id)
 * // [{ id: 2, lastActive: '2024-01-15' }, { id: 3, lastActive: '2024-01-14' }]
 * // Note: Returns elements from activeUsers that have matching IDs in currentUsers
 *
 * // Works with primitive arrays too
 * const numbers1 = [1, 2, 3, 4, 5]
 * const numbers2 = [3, 4, 5, 6, 7]
 * const common = intersectBy(numbers1, numbers2, x => x); // [3, 4, 5]
 * ```
 *
 * @param list1 - The first array (used to build the lookup map)
 * @param list2 - The second array (elements returned from here)
 * @param by - Function to compute the comparison key
 * @returns Elements from list2 that have matching keys in list1
 * @template T - The type of array elements
 */
export function intersectBy<T>(list1: Array<T>, list2: Array<T>, by: (item: T) => string | number): Array<T> {
  const map: Map<string | number, T> = new Map()
  for (const item of list1) {
    const key = by(item)
    map.set(key, item)
  }
  return list2.filter((item) => map.has(by(item)))
}

/**
 * Returns elements from `list1` that don't exist in `list2` based on a key function.
 * Calculates the set difference `list1 - list2`.
 *
 * @description This function performs a set difference operation using a key function
 * to determine equality. It finds elements that exist in the first array but not in
 * the second array, useful for finding missing items, removed elements, or exclusions.
 *
 * @example
 * ```typescript
 * const allUsers = [
 *   { id: 1, name: 'Alice' },
 *   { id: 2, name: 'Bob' },
 *   { id: 3, name: 'Charlie' }
 * ]
 *
 * const bannedUsers = [
 *   { id: 2, reason: 'Spam' },
 *   { id: 4, reason: 'Abuse' }
 * ]
 *
 * const activeUsers = differenceBy(allUsers, bannedUsers, user => user.id)
 * // [{ id: 1, name: 'Alice' }, { id: 3, name: 'Charlie' }]
 *
 * // Find what's been removed
 * const previousItems = ['a', 'b', 'c', 'd']
 * const currentItems = ['b', 'd', 'e']
 * const removedItems = differenceBy(previousItems, currentItems, x => x)
 * // ['a', 'c'] - items that were in previous but not in current
 *
 * const newItems = differenceBy(currentItems, previousItems, x => x)
 * // ['e'] - items that are in current but not in previous
 * ```
 *
 * @param list1 - The array to subtract from
 * @param list2 - The array to subtract (items to exclude)
 * @param by - Function to compute the comparison key
 * @returns Elements from list1 that don't have matching keys in list2
 * @template T - The type of array elements
 */
export function differenceBy<T>(list1: Array<T>, list2: Array<T>, by: (item: T) => string | number): Array<T> {
  const map: Map<string | number, T> = new Map()
  for (const item of list2) {
    const key = by(item)
    map.set(key, item)
  }
  return list1.filter((item) => !map.has(by(item)))
}

/**
 * Calculates the sum of all numbers in an array.
 *
 * @description This function adds up all numeric values in an array. It returns 0 for
 * empty arrays and handles the addition using reduce for optimal performance.
 *
 * @example
 * ```typescript
 * sum([1, 2, 3, 4, 5]); // 15
 * sum([10, -5, 3]); // 8
 * sum([]); // 0
 *
 * // Common use cases
 * const prices = [99.99, 149.50, 79.99]
 * const total = sum(prices); // 329.48
 *
 * const scores = [85, 92, 78, 90]
 * const totalScore = sum(scores); // 345
 * const average = totalScore / scores.length; // 88.75
 *
 * // With extracted values
 * const transactions = getTransactions()
 * const totalAmount = sum(pickFromList(transactions, 'amount'))
 * ```
 *
 * @param list - Array of numbers to sum
 * @returns The sum of all numbers in the array
 */
export function sum(list: Array<number>): number {
  return list.reduce((sum, value) => sum + value, 0)
}

/**
 * Checks if two arrays contain the same elements, ignoring order.
 * Only works with primitive types for reliable equality checking.
 *
 * @description This function compares two arrays to determine if they contain exactly
 * the same elements, regardless of order. It only works with primitive types
 * (string, number, boolean) to ensure reliable equality comparisons.
 *
 * @example
 * ```typescript
 * matchesItems([1, 2, 3], [3, 2, 1]); // true
 * matchesItems([1, 2, 3], [1, 2, 4]); // false
 * matchesItems(['a', 'b', 'c'], ['c', 'a', 'b']); // true
 * matchesItems([true, false], [false, true]); // true
 *
 * // Different lengths
 * matchesItems([1, 2, 3], [1, 2]); // false
 *
 * // Empty arrays
 * matchesItems([], []); // true
 *
 * // Useful for comparing permissions, tags, etc.
 * const userPermissions = ['read', 'write', 'delete']
 * const requiredPermissions = ['write', 'read', 'delete']
 * const hasAllPermissions = matchesItems(userPermissions, requiredPermissions); // true
 *
 * // Comparing filter states
 * const selectedFilters = ['category:electronics', 'price:100-200']
 * const savedFilters = ['price:100-200', 'category:electronics']
 * const filtersMatch = matchesItems(selectedFilters, savedFilters); // true
 * ```
 *
 * @param list1 - First array to compare
 * @param list2 - Second array to compare
 * @returns True if arrays contain the same elements (regardless of order)
 * @template T - The type of array elements (must be primitive)
 */
export function matchesItems<T extends string | number | boolean>(list1: Array<T>, list2: Array<T>): boolean {
  return list1.length === list2.length && list1.every((item) => list2.includes(item))
}

/**
 * Checks if an array contains duplicate values by comparing array length with Set size.
 *
 * @description This function efficiently detects duplicates by creating a Set from the array
 * and comparing sizes. Since Sets only store unique values, a size difference indicates
 * duplicates were present in the original array.
 *
 * @example
 * ```typescript
 * hasDuplicates([1, 2, 3, 4, 5]); // false
 * hasDuplicates([1, 2, 3, 3, 4]); // true
 * hasDuplicates(['a', 'b', 'c']); // false
 * hasDuplicates(['a', 'b', 'a']); // true
 *
 * // Empty array has no duplicates
 * hasDuplicates([]); // false
 *
 * // Validation use cases
 * const usernames = ['alice', 'bob', 'charlie']
 * if (hasDuplicates(usernames)) {
 *   throw new Error('Duplicate usernames found')
 * }
 *
 * // Check data integrity
 * const ids = records.map(r => r.id)
 * if (hasDuplicates(ids)) {
 *   console.warn('Duplicate IDs detected in data')
 * }
 * ```
 *
 * @param list - Array to check for duplicates
 * @returns True if the array contains duplicate values
 * @template T - The type of array elements
 */
export function hasDuplicates<T>(list: Array<T>): boolean {
  return new Set(list).size !== list.length
}

/**
 * Safe version of getting the first element. Returns `null` if the array is empty,
 * undefined, or null. More forgiving than `first()`.
 *
 * @description This function provides null-safe access to the first element of an array-like
 * object. Unlike `first()`, it doesn't throw errors and handles null/undefined inputs gracefully.
 * It works with any array-like object including strings, NodeLists, and arguments objects.
 *
 * @example
 * ```typescript
 * const numbers = [1, 2, 3, 4]
 * head(numbers); // 1
 *
 * head([]); // null
 * head(undefined); // null
 * head(null); // null
 *
 * // Works with array-like objects
 * head('hello'); // 'h'
 * head(document.querySelectorAll('div')); // first div or null
 *
 * // Safe for optional arrays
 * function processItems(items?: number[]) {
 *   const firstItem = head(items)
 *   if (firstItem !== null) {
 *     // Safe to use firstItem
 *     console.log('First item:', firstItem)
 *   }
 * }
 *
 * // Chaining with other operations
 * const result = someArray
 *   .filter(condition)
 *   .map(transform)
 *   .pipe(head); // Returns first result or null
 * ```
 *
 * @param list - Array-like object to get the first element from
 * @returns The first element or null if empty/null/undefined
 * @template T - The type of elements in the array-like object
 */
export function head<T>(list: ArrayLike<T> | undefined | null): T | null {
  if (!list || list.length === 0) return null
  return list[0]
}
